import React from 'react'
import {Typography} from '@mui/material'
import {makeStyles} from '@mui/styles'
import Grid from '@mui/material/Grid'
import {Typography as TypographyStyle, Colors} from './../../assets/styles'
import SelectInput from '../SelectInput'
import AmenityConstants from './AmenitiesConstants'
import ImageGallerySelector from '../ImageGallerySelector'
import DashboardToggle from '../DashboardToggle'
import ManyClosuresInput from './ManyClosuresInput'
import ManyGeneralAvailabilityInput from './ManyGeneralAvailabilityInput'
import NumberInput from '../NumberInput'
import CurrencyInput from '../CurrencyInput'
import PropTypes from 'prop-types'
import TextInput from '../TextInput'
import MultilineTextInput from '../MultilineTextInput'
import {spaceMedium} from '../../assets/styles/spacing'
import UnitAndMagnitudeInput from './UnitAndMagnitudeInput'
import {pluralizeWord} from '../../Utils/StringFormatter'
import {useSelector} from 'react-redux'
import AmenityLinking from './AmenityLinking'

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
  },
  heading: {
    ...TypographyStyle.CaptionHeader,
    lineHeight: '19px',
    color: Colors.rxrRedColor,
  },
  title: {
    ...TypographyStyle.H3Header,
    lineHeight: '22px',
    color: Colors.rxrBlackColor,
  },
  headerTitle: {
    ...TypographyStyle.H3Header,
    lineHeight: '22px',
    marginTop: '14px',
    marginBottom: '30px',

    fontSize: 40,
    color: '#431A1A',
    fontWeight: 'bold',
    fontStyle: 'normal',
  },
}))

// constructs a select options array in the form of [..., {value: 720, label: '12 hours'}, ...] for values 1 to 40, with value 0 having label 'No limit'
const WEEKLY_LIMIT_OPTIONS = [...new Array(41)].map((e, i) => ({
  value: i * 60,
  label: i === 0 ? 'No limit' : pluralizeWord('hour', i, true),
}))

function calculateTimeRange(minTimeSelected, interval) {
  let times = [] // time array
  let tt = parseInt(minTimeSelected) || 15 // start time
  for (let i = 0; tt <= 8 * 60; i++) {
    let obj = {value: null, label: null}

    let hh = Math.floor(tt / 60) // getting hours of day in 0-24 format
    let mm = tt % 60 // getting minutes of the hour in 0-55 format

    if (mm === 0) {
      obj.value = '' + tt
      obj.label = (hh % 12) + ' hour'
    } else if (hh) {
      obj.value = '' + tt
      obj.label = (hh % 12) + ':' + ('0' + mm).slice(-2)
    } else {
      obj.value = '' + tt
      obj.label = mm + ' minutes'
    }
    times[i] = obj
    if (tt === 8 * 60) {
      times[i + 1] = {value: 1440, label: 'All day'}
    }
    tt = tt + interval
  }
  return times
}

function AmenityForm(props) {
  const isMobile = useSelector(state => state.App.isMobile)
  const classes = useStyles()

  return (
    <div className={classes.root}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography>{AmenityConstants.ALL_FIELDS_REQUIRED}</Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography className={classes.title}>{AmenityConstants.TITLE_DETAILS}</Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextInput
            label="Amenity name"
            placeholder=""
            isRequired={true}
            maxLength={120}
            onChange={val => props.updateForm({label: val})}
            value={props.label}
            error={props.invalidItems.includes('label')}
            isDisabled={props.labelDisabled}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ImageGallerySelector
            maxPhotos={3}
            value={props.attachments}
            onChange={v => props.updateForm({attachments: v})}
            isRequired={true}
            error={props.invalidItems.includes('attachments')}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <MultilineTextInput
            label="Description"
            placeholder=""
            rows={4}
            rowsMax={4}
            isRequired={true}
            maxLength={10000}
            onChange={val => props.updateForm({description: val})}
            value={props.description}
            error={props.invalidItems.includes('description')}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SelectInput
            label="Amenity Type"
            onChange={val => props.updateForm({amenityType: val})}
            value={props.amenityType}
            options={[
              {value: 'bike-storage-room', label: 'Bike Storage Room'},
              {value: 'bike-workshop', label: 'Bike Workshop'},
              {value: 'dry-cleaners', label: 'Dry Cleaners'},
              {value: 'laundry', label: 'Laundry'},
              {value: 'lobby', label: 'Lobby'},
              {value: 'mail-room', label: 'Mail Room'},
              {value: 'mothers-room', label: "Mother's Room"},
              {value: 'package-room', label: 'Package Room'},
              {value: 'retail', label: 'Retail'},
              {value: 'storage', label: 'Storage'},
              {value: 'childrens-play-area', label: "Children's Play Area"},
              {value: 'coffee-vestibule', label: 'Coffee Vestibule'},
              {value: 'game-room', label: 'Game Room'},
              {value: 'library', label: 'Library'},
              {value: 'lounge', label: 'Lounge'},
              {value: 'screening-room', label: 'Screening Room'},
              {value: 'dog-run', label: 'Dog Run'},
              {value: 'pet-spa', label: 'Pet Spa'},
              {value: 'aerobics-studio', label: 'Aerobics Studio'},
              {value: 'fitness-center', label: 'Fitness Center (exercise room)'},
              {value: 'locker-room', label: 'Locker Room'},
              {value: 'massage-room', label: 'Massage Room'},
              {value: 'peleton-bike-room', label: 'Peleton Bike Room'},
              {value: 'sauna', label: 'Sauna'},
              {value: 'yoga-room', label: 'Yoga Room'},
              {value: 'business-center', label: 'Business Center'},
              {value: 'co-working-space', label: 'Co-working Space'},
              {value: 'meeting-room', label: 'Meeting Room'},
              {value: 'phone-work-booth', label: 'Phone/Work Booth'},
              {value: 'bar', label: 'Bar'},
              {value: 'club-room', label: 'Club Room'},
              {value: 'kitchen-area', label: 'Kitchen Area'},
              {value: 'private-dining', label: 'Private Dining'},
              {value: 'restaurant', label: 'Restaurant'},
              {value: 'speakeasy', label: 'Speakeasy'},
              {value: 'cabana', label: 'Cabana'},
              {value: 'gazebo', label: 'Gazebo( landscaped terrace)'},
              {value: 'green-space', label: 'Green Space'},
              {value: 'grilling-area', label: 'Grilling Area'},
              {value: 'outdoor-fitness-area', label: 'Outdoor Fitness Area'},
              {value: 'outdoor-kids-play-area', label: 'Outdoor Kids Play Area'},
              {value: 'outdoor-movie-area', label: 'Outdoor Movie Area (general exercise area)'},
              {value: 'outdoor-sitting-area', label: 'Outdoor Sitting Area'},
              {value: 'pool', label: 'Pool'},
              {value: 'pool-area', label: 'Pool Area'},
              {value: 'rooftop', label: 'Rooftop'},
              {value: 'thebulletin.io', label: 'TheBulletin.io'},
              {value: 'coffee', label: 'Coffee'},
              {value: 'electric-car-rental', label: 'Electric Car Rental'},
              {value: 'ev-chargers', label: 'EV Chargers'},
              {value: 'full-time-concierge', label: 'Full Time Concierge'},
              {value: 'micromobility-rental', label: 'Micromobility Rental'},
              {value: 'rentbrella', label: 'Rentbrella'},
              {value: 'tulu', label: 'Tulu'},
            ]}
            isRequired={true}
            error={props.invalidItems.includes('amenityType')}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <MultilineTextInput
            label="Amenity rules for residents"
            placeholder=""
            helperText="10,000 character limit"
            rows={4}
            rowsMax={4}
            maxLength={10000}
            onChange={val => props.updateForm({rules: val})}
            value={props.rules}
          />
        </Grid>
        <Grid item xs={12}>
          <DashboardToggle
            label={
              props.mustAgreeToRules
                ? 'Residents must agree to rules before booking'
                : 'Residents do not need to agree to rules before booking'
            }
            value={props.mustAgreeToRules}
            onChange={isSelected => props.updateForm({mustAgreeToRules: isSelected})}
          />
        </Grid>
        <Grid item xs={12}>
          <DashboardToggle
            label={
              props.ignoreBookInAdvanceForStaff
                ? 'Ignore "book in advance by" rules for staff users'
                : 'Do not ignore "book in advance by" rules for staff users'
            }
            value={props.ignoreBookInAdvanceForStaff}
            onChange={isSelected => props.updateForm({ignoreBookInAdvanceForStaff: isSelected})}
          />
        </Grid>

        <UnitAndMagnitudeInput
          label={'Resident has to cancel by'}
          value={props.advancedNoticeCancel}
          onChange={val => props.updateForm({advancedNoticeCancel: val})}
          isRequired={true}
          hasError={props.invalidItems.includes('advancedNoticeCancel')}
        />
        <Grid item xs={0} md={6}></Grid>

        <UnitAndMagnitudeInput
          label={'Book in advance by'}
          value={props.advancedNoticeRequest}
          onChange={val => props.updateForm({advancedNoticeRequest: val})}
          isRequired={true}
          hasError={props.invalidItems.includes('advancedNoticeRequest')}
        />
        <Grid item xs={0} md={6}></Grid>

        <UnitAndMagnitudeInput
          label={'Reservation buffer time'}
          value={props.reservationPadding}
          onChange={val => props.updateForm({reservationPadding: val})}
          isRequired={true}
          hasError={props.invalidItems.includes('reservationPadding')}
        />
        <Grid item xs={0} md={6}></Grid>

        <Grid item xs={12}>
          <Typography className={classes.title}>{AmenityConstants.TITLE_PAID_RESERVATION}</Typography>
        </Grid>
        <Grid item xs={12} md={3}>
          <DashboardToggle
            label={props.requiresPayment ? 'This is a paid reservation' : 'This is not a paid reservation'}
            value={props.requiresPayment}
            onChange={requiresPaymentValue => {
              const updateData = {requiresPayment: requiresPaymentValue}

              // Automatically set needsApproval to true when requiresPayment is true
              if (requiresPaymentValue) {
                updateData.needsApproval = true
              }

              props.updateForm(updateData)
            }}
          />
        </Grid>
        <Grid item xs={0} md={9}></Grid>

        <Grid item xs={12} md={3}>
          <CurrencyInput
            label="Hourly rate"
            min={0}
            onChange={val => props.updateForm({hourlyPrice: val})}
            value={props.hourlyPrice}
            isRequired={props.requiresPayment}
            isDisabled={!props.requiresPayment}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <CurrencyInput
            label="Deposit"
            min={0}
            onChange={val => props.updateForm({depositPrice: val})}
            value={props.depositPrice}
            isRequired={props.requiresPayment}
            isDisabled={!props.requiresPayment}
          />
        </Grid>

        <Grid item xs={0} md={6}></Grid>

        {/* Linked Amenities Section */}
        {props.id && (
          <React.Fragment>
            <Grid item xs={12} md={7}>
              <AmenityLinking
                amenityId={props.id}
                linkedAmenities={props.linkedAmenities}
                onLinksChange={props.onLinksChange}
                isSaving={props.isSaving}
              />
            </Grid>
            <Grid item xs={0} md={5}></Grid>
          </React.Fragment>
        )}

        <Grid item xs={12}>
          <Typography className={classes.title}>{AmenityConstants.TITLE_RULES}</Typography>
        </Grid>
        <Grid item xs={12} md={3}>
          <NumberInput
            label="Amenity maximum occupancy"
            min={1}
            onChange={val => props.updateForm({maxOccupancy: val})}
            value={props.maxOccupancy}
            isRequired={true}
            integerOnly={true}
            error={props.invalidItems.includes('maxOccupancy')}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <NumberInput
            label="Maximum guests per booking"
            min={0}
            helperText={'A value of 0 means the resident cannot bring guests'}
            onChange={val => props.updateForm({maxPartySize: val + 1})}
            value={props.maxPartySize - 1}
            isRequired={true}
            integerOnly={true}
            error={props.invalidItems.includes('maxPartySize')}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <SelectInput
            onChange={val =>
              props.updateForm({
                minReservationDuration: val,
              })
            }
            label={'Minimum reservation duration'}
            isRequired={true}
            options={AmenityConstants.MINIMUM_RESERVATION_OPTIONS}
            value={'' + props.minReservationDuration}
            error={props.invalidItems.includes('minReservationDuration')}
          />
        </Grid>

        <Grid item xs={0} md={3}></Grid>

        <Grid item xs={12} md={3}>
          <SelectInput
            onChange={val =>
              props.updateForm({
                maxReservationDuration: val,
              })
            }
            label={'Maximum reservation duration'}
            isRequired={true}
            options={calculateTimeRange(props.minReservationDuration, props.reservationIncrement)}
            value={'' + props.maxReservationDuration}
            error={props.invalidItems.includes('maxReservationDuration')}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <NumberInput
            label="Max concurrent reservations"
            min={1}
            helperText="A value of 1 means only 1 reservation can occur at a time"
            onChange={val => props.updateForm({maxConcurrentReservations: val})}
            value={props.maxConcurrentReservations}
            isRequired={true}
            integerOnly={true}
            error={props.invalidItems.includes('maxConcurrentReservations')}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <SelectInput
            label={'Weekly reservation limit'}
            onChange={v => props.updateForm({weeklyReservationLimit: v})}
            options={WEEKLY_LIMIT_OPTIONS}
            value={props.weeklyReservationLimit || '0'}
            isRequired={true}
            hasError={props.invalidItems.includes('weeklyReservationLimit')}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography className={classes.title}>{AmenityConstants.TITLE_RESTRICTIONS}</Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <DashboardToggle
            label={
              props.needsApproval
                ? 'This amenity requires approval before booking'
                : 'This amenity does not require approval before booking'
            }
            value={props.needsApproval}
            onChange={needsApprovalValue => {
              const updateData = {needsApproval: needsApprovalValue}

              // Automatically set requiresPayment to false when needsApproval is false
              if (!needsApprovalValue) {
                updateData.requiresPayment = false
              }

              props.updateForm(updateData)
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <div style={{marginBottom: spaceMedium, maxWidth: isMobile ? '100%' : '75%'}}>
            <ManyGeneralAvailabilityInput
              value={props.generalAvailability}
              onChange={a => props.updateForm({generalAvailability: a})}
              onRemove={props.onRemoveGeneralAvailability}
              increment={props.reservationIncrement}
              error={props.invalidItems.includes('generalAvailability')}
            />
          </div>
        </Grid>

        <Grid item xs={12}>
          <Typography className={classes.title}>{AmenityConstants.TITLE_CLOSURES}</Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography>{AmenityConstants.DESCRIPTION_CLOSURES}</Typography>
        </Grid>

        <Grid item xs={12}>
          <div style={{marginBottom: spaceMedium, maxWidth: isMobile ? '100%' : '75%'}}>
            <ManyClosuresInput value={props.closures} onChange={c => props.updateForm({closures: c})} onRemove={props.onRemoveClosure} />
          </div>
        </Grid>
      </Grid>
    </div>
  )
}

AmenityForm.propTypes = {
  updateForm: PropTypes.func.isRequired,
  onRemoveGeneralAvailability: PropTypes.func.isRequired,
  onRemoveClosure: PropTypes.func.isRequired,
  mode: PropTypes.string.isRequired,
  invalidItems: PropTypes.array.isRequired,
  requiresPayment: PropTypes.bool,
  hourlyPrice: PropTypes.number,
  depositPrice: PropTypes.number,
  linkedAmenities: PropTypes.array,
  onLinksChange: PropTypes.func,
  isSaving: PropTypes.bool,
  amenityType: PropTypes.string,
}

export default AmenityForm
